
import React from 'react';
import { CircleCheck, Users } from 'lucide-react';
import useScrollReveal from '@/hooks/useScrollReveal';

const StepCard = ({ number, title, description }: { number: string, title: string, description: string }) => {
  return (
    <div className="relative stagger-item">
      <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] flex items-center justify-center font-bold text-lg animate-pulse-glow">
        {number}
      </div>
      <div className="pl-16">
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-white/70">{description}</p>
      </div>
    </div>
  );
};

const HowItWorksSection = () => {
  const titleRef = useScrollReveal();
  const stepsRef = useScrollReveal({ threshold: 0.2, staggerDelay: 200 });
  const advantagesRef = useScrollReveal({ threshold: 0.3 });
  
  return (
    <section id="how-it-works" className="py-20 px-4 bg-black/80">
      <div className="container mx-auto">
        <div className="text-center mb-16 reveal" ref={titleRef}>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">How It Works</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Getting started with AI Wealth Manager is simple. Our advanced technology and expert advisors work together to manage your entire investment portfolio.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="space-y-16" ref={stepsRef}>
            <StepCard 
              number="01"
              title="Sign Up for Early Access"
              description="Register with your email to secure your spot and get exclusive early access to our platform with additional benefits."
            />
            
            <StepCard 
              number="02"
              title="Complete Your Financial Profile"
              description="Share your financial goals, risk tolerance, and current investments across all asset classes."
            />
            
            <StepCard 
              number="03"
              title="AI Creates Your Strategy"
              description="Our algorithms develop a personalized investment strategy optimized across stocks, real estate, gold, bonds, and other assets."
            />
            
            <StepCard 
              number="04"
              title="Get Human Support When Needed"
              description="Schedule consultations with dedicated wealth managers for strategic guidance and personalized advice."
            />
          </div>
          
          <div className="glass-card rounded-xl p-8 bg-card-gradient h-fit reveal transform transition-all duration-500 hover:shadow-lg hover:shadow-[#9E7C47]/30 hover:border-[#9E7C47]/30" ref={advantagesRef}>
            <h3 className="text-2xl font-semibold mb-6">The Hybrid Advantage</h3>
            
            <ul className="space-y-4">
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">AI-powered portfolio optimization across multiple asset classes</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Access to certified wealth managers for strategic guidance</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Diverse investment opportunities beyond just stocks</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Bank-level security and encryption for your data</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Personalized insights for each asset class in your portfolio</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <CircleCheck className="h-6 w-6 text-[#9E7C47] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Premium features at no additional cost for early adopters</span>
              </li>
              
              <li className="flex items-start hover:translate-x-1 transition-transform duration-300">
                <Users className="h-6 w-6 text-[#F9F1A9] mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-white/90">Dedicated wealth manager assigned to premium accounts</span>
              </li>
            </ul>
            
            <div className="mt-8 p-4 rounded-lg bg-[#9E7C47]/10 border border-[#9E7C47]/30 transform transition-all duration-300 hover:bg-[#9E7C47]/20">
              <p className="text-white/90 text-sm">
                <strong>Our Promise:</strong> We combine the best of AI technology and human expertise to create a wealth management experience that's personalized, diversified, and effective across all asset classes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
